import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Users, ShieldCheck } from "lucide-react";
import { PRICING, AFFILIATE_TIERS } from "../data";
import { useBooking } from "../BookingContext";

function PlanCard({ plan, featured, onSelect }) {
  return (
    <div
      className={`relative flex flex-col p-8 bg-[#0C142A] border transition-all hover:-translate-y-1 ${
        featured ? "border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.15)]" : "border-[#1D2847] hover:border-[#D4AF37]/50"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-8 inline-flex items-center gap-1 bg-[#D4AF37] text-[#050A15] text-xs font-bold uppercase tracking-wider px-3 py-1">
          <Star size={12} /> Most Popular
        </span>
      )}
      <h4 className="font-display text-xl font-bold tracking-tight">{plan.name}</h4>
      <div className="mt-4 flex items-end gap-1">
        <span className="font-display text-4xl font-black gold-text">{plan.price}</span>
        <span className="text-[#A3A8B8] mb-1 text-sm">{plan.unit}</span>
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#F8F9FA]">
            <Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => onSelect(plan.name)}
        className={`mt-8 font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors ${
          featured
            ? "bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B]"
            : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
        }`}
      >
        Enroll / Inquire
      </button>
    </div>
  );
}

export default function Pricing() {
  const [tab, setTab] = useState("nonAffiliate");
  const { openBooking } = useBooking();
  const active = PRICING[tab];

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-3xl mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#D4AF37]" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#D4AF37]">OSHA Pricing</span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight leading-[0.95]">
          Transparent <span className="gold-text">Certification</span> Pricing
        </h2>
        <p className="mt-5 text-[#A3A8B8] flex items-start gap-2" data-testid="pricing-compliance-note">
          <ShieldCheck size={20} className="text-[#D4AF37] shrink-0 mt-0.5" />
          All courses are full two-day OSHA 10 &amp; 30 certifications, delivered in strict accordance with
          OSHA Outreach Training Program guidelines. We do not offer one-day OSHA training.
        </p>
      </div>

      <div className="inline-flex border border-[#1D2847] mb-12">
        {Object.entries(PRICING).map(([key, val]) => (
          <button
            key={key}
            data-testid={`pricing-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === key ? "bg-[#D4AF37] text-[#050A15]" : "text-[#A3A8B8] hover:text-[#D4AF37]"
            }`}
          >
            {val.label}
          </button>
        ))}
      </div>

      <p className="text-[#A3A8B8] mb-8 -mt-6">{active.note}</p>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        {active.plans.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <PlanCard plan={plan} featured={i === 0} onSelect={openBooking} />
          </motion.div>
        ))}
      </div>

      {/* Affiliate / Corporate program */}
      <div className="mt-24">
        <div className="flex items-center gap-3 mb-4">
          <Users className="text-[#D4AF37]" size={22} />
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Become an Affiliate — Corporate Safety Programs</h3>
        </div>
        <p className="text-[#A3A8B8] max-w-3xl mb-8">
          Ongoing on-site safety partnership. 24-month agreement with monthly visits, cloud-documented
          inspection notes, photos, and action items emailed directly to your safety lead and leadership.
          <span className="text-[#F8F9FA]"> Residual 3% increase every 2 years.</span>
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1D2847] border border-[#1D2847]">
          {AFFILIATE_TIERS.map((t, i) => (
            <motion.button
              key={t.range}
              onClick={() => openBooking("Affiliate Safety Program")}
              data-testid={`affiliate-tier-${i}`}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group bg-[#0C142A] p-7 text-left hover:bg-[#101B36] transition-colors"
            >
              <div className="text-xs uppercase tracking-wider text-[#A3A8B8]">{t.range}</div>
              <div className="mt-3 font-display text-3xl font-black gold-text">{t.price}<span className="text-sm text-[#A3A8B8] font-normal"> {t.per}</span></div>
              <div className="mt-3 flex items-center gap-2 text-sm text-[#F8F9FA]">
                <Check size={15} className="text-[#D4AF37]" /> {t.visits}
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
